import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { CardGrid, Filters, Pagination, Pills } from "..";
import { useApi, useUtilities, useEventTypeUrl, usePageUrl, useWhenUrl, useWhereUrl } from "../../hooks";
import "./EventSearch.scss";

const DEFAULT_IMAGE =
  "https://traveloregon.com/wp-content/uploads/2017/08/Thumbnail_TO_placeholder-image_4.jpg";

export const EventSearch: React.FC = () => {
  const { fetchEvents } = useApi();
  const { currentEventType, setEventType, isLoading: isEventTypeLoading } = useEventTypeUrl();
  const { currentWhen, setWhen } = useWhenUrl();
  const { currentWhere, setWhere, isLoading: isWhereLoading } = useWhereUrl();
  const {
    extractEyebrowFromPostSource,
    processDateFromPostSource,
    extractLocationFromPostSource,
    formatDate,
    colorByCategoryMap,
  } = useUtilities();

  // Add ref for scroll target
  const cardGridRef = useRef<HTMLDivElement>(null);

  const [events, setEvents] = useState<any[]>([]);
  const [rawEventResponse, setRawEventResponse] = useState<any>();
  const [totalNumEvents, setTotalNumEvents] = useState<number>(0);
  // const [currentPage, setCurrentPage] = useState(1);
  const { currentPage, setPage: setCurrentPage } = usePageUrl();
  const [isInitialized, setIsInitialized] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);

  // Ref to track and cancel ongoing requests
  const abortControllerRef = useRef<AbortController | null>(null);

  const [filterValues, setFilterValues] = useState<{
    eventType?: { value: string; label: string };
    where?: {
      latitude: string;
      longitude: string;
      label: string;
      locationType: string;
      locationId: string;
    };
    when?: Date | { startDate: string; endDate: string; label: string };
  }>({});

  // Sync URL filters with local filter state and mark as initialized
  useEffect(() => {
    // Wait for async loading to be finished before setting the filters.
    if (!isEventTypeLoading && !isWhereLoading) {
      setFilterValues(prev => ({
        ...prev,
        eventType: currentEventType,
        when: currentWhen,
        where: currentWhere
      }));
      setIsInitialized(true);
    }
  }, [currentEventType, currentWhen, currentWhere, isEventTypeLoading, isWhereLoading]);

  const pills = useMemo(() => {
    const result: { id: any; label: string; field: string }[] = [];
    if (filterValues.eventType)
      result.push({
        id: filterValues.eventType.value,
        label: filterValues.eventType.label,
        field: "eventType",
      });
    if (filterValues.where)
      result.push({
        id: filterValues.where.label,
        label: filterValues.where.label,
        field: "where",
      });
    if (filterValues.when)
      result.push({
        id: typeof filterValues.when === 'object' && 'startDate' in filterValues.when 
            ? filterValues.when.startDate + '-' + filterValues.when.endDate 
            : (filterValues.when as Date).getTime(),
        label: typeof filterValues.when === 'object' && 'label' in filterValues.when 
            ? filterValues.when.label 
            : formatDate(filterValues.when as Date),
        field: "when",
      });
    return result;
  }, [filterValues, formatDate]);

  const fetchData = useCallback(async () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const { eventType, where, when } = filterValues;

      let timeStampParam: string | undefined = undefined;
      let weekStartDateParam: string | undefined = undefined;
      let weekEndDateParam: string | undefined = undefined;

      if (when) {
        if (when instanceof Date) {
          timeStampParam = Math.round(when.getTime() / 1000).toString();
        } else if (typeof when === 'object' && 'startDate' in when && 'endDate' in when) {
          weekStartDateParam = when.startDate;
          weekEndDateParam = when.endDate;
        }
      }

      const results = await fetchEvents(
        currentPage,
        eventType?.value ?? "",
        where?.latitude ?? "",
        where?.longitude ?? "",
        where?.locationType ?? "",
        where?.locationId ?? "",
        timeStampParam,
        weekStartDateParam,
        weekEndDateParam
      );

      // Only set results if request wasn't cancelled
      if (!abortController.signal.aborted) {
        setRawEventResponse(results);
      }
    } catch (error) {
      // Ignore aborted requests
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Fetch error:', error);
      }
    }
  }, [fetchEvents, filterValues, currentPage]);

  useEffect(() => {
    // Only fetch after initialization is complete
    if (isInitialized) {
      fetchData();
    }
  }, [fetchData, isInitialized]);

  useEffect(() => {
    if (!rawEventResponse) return;
    setEvents(
      rawEventResponse.hits.map((post: any) => ({
        post_id: post._source.post_id,
        image: post._source.photos?.[0]?.image_url ?? DEFAULT_IMAGE,
        date: processDateFromPostSource(post._source),
        location: extractLocationFromPostSource(post._source),
        eyebrow: filterValues.eventType ? filterValues.eventType.label : extractEyebrowFromPostSource(post._source)?.name ?? "Event",
        eyebrowColor: filterValues.eventType
          ? (colorByCategoryMap as any)[Number(filterValues.eventType.value)] ?? undefined
          : extractEyebrowFromPostSource(post._source)?.color ?? undefined,
        url: post._source.permalink,
        title: post._source.post_title,
        summary: post._source.post_excerpt,
        hide_media: post._source.hide_media,
      }))
    );
    setTotalNumEvents(rawEventResponse?.total ?? 0);
    // eslint-disable-next-line
  }, [rawEventResponse]);

  const updateFilters = (field: string, value: any) => {
    if (field === 'eventType') {
      setEventType(value || undefined);
    } else if (field === 'when') {
      setWhen(value || undefined);
    } else if (field === 'where') {
      setWhere(value || undefined);
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterValues({});
    setEventType(undefined);
    setWhen(undefined);
    setWhere(undefined);
    setResetTrigger(prev => prev + 1); // Trigger DatePicker reset
    setCurrentPage(1);
  };

  const onRemoveFilter = (_: string, field: string) => {
    if (field === 'eventType') {
      setEventType(undefined);
    } else if (field === 'when') {
      setWhen(undefined);
      setResetTrigger(prev => prev + 1);
    } else if (field === 'where') {
      setWhere(undefined);
    }
    setCurrentPage(1);
  };

  // Enhanced page change handler with scroll
  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);

    // Scroll to the card grid after a short delay to ensure content has updated
    setTimeout(() => {
      if (cardGridRef.current) {
        cardGridRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  }, [setCurrentPage]);

  return (
    <div className="event-search container-twelve">
      <h2 className="event-search__heading">Find Events</h2>
      <div
        className="event-search__filters-container"
        style={{ overflow: "visible" }}
      >
        <Filters
          filterValues={filterValues as any}
          changeHandler={updateFilters}
          resetTrigger={resetTrigger}
        />
      </div>
      <div className="event-search__content" ref={cardGridRef}>
        <h3 className="event-search__title">
          {totalNumEvents} {totalNumEvents === 1 ? "Event" : "Events"}
        </h3>
        {pills.length > 0 && (
          <Pills
            filters={pills}
            onClearFilters={clearFilters}
            onRemoveFilter={onRemoveFilter}
          />
        )}
        <CardGrid events={events} />
        <Pagination
          currentPage={currentPage}
          totalPages={rawEventResponse?.total_pages}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};
